import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import type { TeacherAssignment, Teacher } from '@skjv/common';

interface TeacherAssignmentEditorProps {
  teacherAssignments: TeacherAssignment[];
  studyGroupId?: string;
  onChange: (assignments: TeacherAssignment[]) => void;
}

/**
 * Teacher assignment editor - shows teachers from the study group with review questions.
 */
export const TeacherAssignmentEditor = observer(function TeacherAssignmentEditor({
  teacherAssignments,
  studyGroupId,
  onChange,
}: TeacherAssignmentEditorProps) {
  const studyGroup = studyGroupId
    ? bibleState.studyGroups.find((g) => g.id === studyGroupId)
    : undefined;
  const teachers = studyGroup?.teachers || [];

  // Auto-sync teacher assignments when study group changes
  useEffect(() => {
    if (!studyGroupId || teachers.length === 0) return;

    const existingByTeacherId = new Map(teacherAssignments.map((a) => [a.teacher.id, a]));

    const synced: TeacherAssignment[] = teachers.map((teacher) => {
      const existing = existingByTeacherId.get(teacher.id);
      if (existing) {
        return { ...existing, teacher: { id: teacher.id, name: teacher.name } };
      }
      return { teacher: { id: teacher.id, name: teacher.name }, reviewQuestion: null };
    });

    if (JSON.stringify(synced) !== JSON.stringify(teacherAssignments)) {
      onChange(synced);
    }
  }, [studyGroupId, teachers, teacherAssignments, onChange]);

  const handleQuestionChange = (teacherId: string, question: string) => {
    const updated = teacherAssignments.map((a) => {
      if (a.teacher.id !== teacherId) return a;
      return { ...a, reviewQuestion: question || null };
    });
    onChange(updated);
  };

  const getAssignment = (teacher: Teacher): TeacherAssignment | undefined => {
    return teacherAssignments.find((a) => a.teacher.id === teacher.id);
  };

  if (!studyGroupId || teachers.length === 0) return null;

  return (
    <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
      <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
        Teacher Assignments ({teachers.length})
      </h3>

      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Add review questions for each teacher.
      </p>

      <div className="space-y-3">
        {teachers.map((teacher) => {
          const assignment = getAssignment(teacher);
          return (
            <div
              key={teacher.id}
              className="rounded-lg border border-stone-200/30 bg-white/50 p-3 dark:border-stone-700/20 dark:bg-stone-900/30"
            >
              <div className="mb-2 text-base font-medium text-stone-800 dark:text-stone-200">
                {teacher.name}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                  Review Question
                </label>
                <textarea
                  value={assignment?.reviewQuestion || ''}
                  onChange={(e) => handleQuestionChange(teacher.id, e.target.value)}
                  placeholder="Ask a discussion question for this teacher..."
                  rows={3}
                  className="w-full resize-none rounded-lg border border-stone-300/50 bg-white/80 px-2.5 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600/50 dark:bg-stone-800/60 dark:text-stone-200 dark:placeholder-stone-500"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
